import React, { useEffect, useState } from 'react';
import { postData } from '@/features/shared/lib/api';
import KeywordCloud from '@/features/researcher/components/charts/KeywordCloud';
import ScholarOutput5YearChart from '@/features/researcher/components/charts/ScholarOutput5YearChart';
import CollaborationDonut from '@/features/researcher/components/charts/CollaborationDonut';
import MajorJournalsBar from '@/features/researcher/components/charts/MajorJournalsBar';
import MajorPartnersTreemap from '@/features/researcher/components/charts/MajorPartnersTreemap';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export default function ScholarMetricsTab({ record }) {
  // 키워드 클라우드 데이터 상태
  const [keywordData, setKeywordData] = useState([]);
  const [keywordLoading, setKeywordLoading] = useState(false);
  // 현재 사용자의 scholar_id (마이페이지에서는 rmVars.scholar_id 사용)
  const userId = typeof rmVars !== 'undefined' && rmVars?.scholar_id ? rmVars.scholar_id : null;

  // record에서 데이터 추출
  const allAchievement = record?.research_all_achievement || {};
  const last5YearAchievement = record?.research_last5year_achievement || {};

  // 키워드 클라우드 API 호출
  useEffect(() => {
    if (!userId) return;

    const fetchKeywordCloud = async () => {
      setKeywordLoading(true);
      try {
        // POST 요청으로 cloudType과 query 전달
        const requestBody = {
          cloudType: 'scholar',
          query: {
            scholar_id: userId,
          },
        };

        const response = await postData('/rm/v1/stats/keyword_cloud', requestBody);

        // API 응답에서 records 추출
        let rawRecords = [];
        if (response?.records && Array.isArray(response.records)) {
          rawRecords = response.records;
        } else if (Array.isArray(response)) {
          rawRecords = response;
        }

        const keywords = rawRecords.map((item) => ({
          keyword: item.text || item.keyword || item.label || '',
          count: item.size || item.count || item.value || 1,
        })).filter((item) => item.keyword); // 빈 키워드 제거

        setKeywordData(keywords);
      } catch (error) {
        console.error('Error fetching keyword cloud:', error);
        console.error('Error details:', error.response?.data || error.message);
        setKeywordData([]);
      } finally {
        setKeywordLoading(false);
      }
    };

    fetchKeywordCloud();
  }, [userId]);

  return (
    <div className="active pt-[50px]  lg:pt-[60px]">
      <div>
        <h3 className="mb-4 text-[18px] font-bold leading-[100%]">Research Achievement</h3>

        <div className="overflow-x-auto">
          <table className="min-w-[800px]">
            <thead>
              <tr>
                <th></th>
                <th>Publications</th>
                <th>Avg · Citation</th>
                <th>Publications<br />In Top 10 Journal</th>
                <th>Single</th>
                <th>International</th>
                <th>SCIs</th>
                <th>Scopus</th>
                <th>KCI</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td className="text-left">All</td>
                <td>{allAchievement.resource_count ?? '-'}</td>
                <td>{allAchievement.avg_citation ?? '-'}</td>
                <td>{allAchievement.in_top10_journal ?? '-'}</td>
                <td>{allAchievement.sole_author_count ?? '-'}</td>
                <td>{allAchievement.international_count ?? '-'}</td>
                <td>{allAchievement.SCI ?? '-'}</td>
                <td>{allAchievement.Scopus ?? '-'}</td>
                <td>{allAchievement.KCI ?? '-'}</td>
              </tr>

              <tr>
                <td className="text-left">5 Year</td>
                <td>{last5YearAchievement.resource_count ?? '-'}</td>
                <td>{last5YearAchievement.avg_citation ?? '-'}</td>
                <td>{last5YearAchievement.in_top10_journal ?? '-'}</td>
                <td>{last5YearAchievement.sole_author_count ?? '-'}</td>
                <td>{last5YearAchievement.international_count ?? '-'}</td>
                <td>{last5YearAchievement.SCI ?? '-'}</td>
                <td>{last5YearAchievement.Scopus ?? '-'}</td>
                <td>{last5YearAchievement.KCI ?? '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-[50px] lg:mt-[60px]">
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Keyword Cloud</h3>
        <div className="relative p-[40px] border border-[#E4E8F1] rounded-[12px]">
          {keywordLoading && (
            <ChartSkeletonLoader show={keywordLoading} />
          )}
          {!keywordLoading && Array.isArray(keywordData) && keywordData.length > 0 ? (
            <KeywordCloud items={keywordData} maxWordsCollapsed={100} maxWords={0} />
          ) : !keywordLoading ? (
            <div className="py-6 text-center text-[13px] font-semibold text-[#8D9AAF]">No keywords</div>
          ) : null}
        </div>
      </div>

      <div className="mt-[50px] lg:mt-[60px] 2xl:flex 2xl:gap-10">
        <div className="2xl:w-[calc((100%-40px)/2)]">

          <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">5 Years Researcher’s Resources Output</h3>
          <div className="p-[24px] border border-[#E4E8F1] rounded-[12px]">
            <ScholarOutput5YearChart scholarId={userId} />
          </div>
        </div>

        <div className="mt-[50px] lg:mt-[60px] 2xl:mt-0 2xl:w-[calc((100%-40px)/2)]">
          <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Collaboration</h3>
          <div className="p-[24px] border border-[#E4E8F1] rounded-[12px]">
            <CollaborationDonut scholarId={userId} />
          </div>
        </div>
      </div>

      <div className="mt-[50px] lg:mt-[60px]">
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">List of Major Publication Journals</h3>
        <div className="p-[24px] border border-[#E4E8F1] rounded-[12px]">
          <MajorJournalsBar scholarId={userId} />
        </div>
      </div>

      <div className="mt-[50px] lg:mt-[60px]">
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Major Partner Research Institutions</h3>
        <div className="p-[24px] border border-[#E4E8F1] rounded-[12px]">
          <MajorPartnersTreemap scholarId={userId} />
        </div>
      </div>
    </div>
  );
}
